import { ToolItem } from '../types';

export interface MiscToolItem extends ToolItem {
  miscCategory: 'generators-random' | 'hardware-testers' | 'clocks-timers' | 'converters-math';
  miscCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const MISC_CATEGORIES_TABS = [
  { id: 'all', label: 'All Miscellaneous Tools', icon: 'Grid' },
  { id: 'generators-random', label: 'Generators & Random', icon: 'Sparkles' },
  { id: 'hardware-testers', label: 'Hardware & Screen Testers', icon: 'Monitor' },
  { id: 'clocks-timers', label: 'Clocks & Timers', icon: 'Clock' },
  { id: 'converters-math', label: 'Encodings & Math', icon: 'Calculator' }
];

export const MISC_TOOLS_DATA: MiscToolItem[] = [
  // --- GENERATORS & RANDOM (15 Tools) ---
  {
    id: 'qr-generator',
    name: 'QR Code Generator',
    description: 'Generate custom QR codes for URLs, WiFi networks, vCards, emails, and plain text.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'QrCode',
    isPopular: true,
    actionType: 'qr'
  },
  {
    id: 'barcode-generator',
    name: 'Barcode Generator',
    description: 'Create high-resolution barcodes (CODE128, EAN, UPC, CODE39) for product labeling.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Barcode',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'barcode-reader',
    name: 'Barcode & QR Reader',
    description: 'Scan or upload image files to decode QR codes and linear barcodes instantly.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Scan',
    actionType: 'generic'
  },
  {
    id: 'uuid-generator',
    name: 'UUID / GUID Generator',
    description: 'Generate cryptographically unique v4 and v1 UUID strings in bulk.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'KeyRound',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'nano-id-generator',
    name: 'Nano ID Generator',
    description: 'Generate compact, URL-friendly, unique ID strings with customizable alphabets.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'random-name',
    name: 'Random Name Generator',
    description: 'Generate realistic first names, last names, usernames, and fantasy character names.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'UserCheck',
    actionType: 'generic'
  },
  {
    id: 'random-number',
    name: 'Random Number Generator',
    description: 'Generate uniform random integers or decimals within custom min and max bounds.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Dices',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'random-password',
    name: 'Random Password Generator',
    description: 'Create ultra-secure passwords with custom lengths, symbols, and readable characters.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Shield',
    isPopular: true,
    actionType: 'password'
  },
  {
    id: 'random-color',
    name: 'Random Color Generator',
    description: 'Discover random color palettes, HEX codes, RGB tuples, and color harmonies.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Palette',
    actionType: 'generic'
  },
  {
    id: 'random-gradient',
    name: 'Random Gradient Generator',
    description: 'Generate vibrant linear and radial CSS background gradients with copyable code.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Blend',
    actionType: 'generic'
  },
  {
    id: 'random-quote',
    name: 'Random Quote Generator',
    description: 'Inspiring quotes across philosophy, science, leadership, and technology.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Quote',
    actionType: 'generic'
  },
  {
    id: 'random-emoji',
    name: 'Random Emoji Generator',
    description: 'Pick random emojis by category or copy funny emoji combos.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Smile',
    actionType: 'generic'
  },
  {
    id: 'dice-roller',
    name: 'Dice Roller',
    description: 'Simulate tabletop RPG dice rolls (D4, D6, D8, D10, D12, D20, D100) with sum totals.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Box',
    actionType: 'generic'
  },
  {
    id: 'coin-flip',
    name: 'Coin Flip Simulator',
    description: '3D coin toss decision maker with heads or tails stats counter.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Coins',
    actionType: 'generic'
  },
  {
    id: 'wheel-spinner',
    name: 'Wheel Spinner',
    description: 'Interactive customizable decision wheel spinner for raffles and random picks.',
    miscCategory: 'generators-random',
    miscCategoryLabel: 'Generators & Random',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Disc',
    actionType: 'generic'
  },

  // --- HARDWARE & SCREEN TESTERS (10 Tools) ---
  {
    id: 'typing-speed-test',
    name: 'Typing Speed Test',
    description: 'Test your typing speed in Words Per Minute (WPM) and accuracy with live statistics.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Keyboard',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'mouse-tester',
    name: 'Mouse Tester',
    description: 'Test left, right, middle mouse clicks, scroll wheel, double click speed, and side buttons.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'MousePointer',
    actionType: 'generic'
  },
  {
    id: 'keyboard-tester',
    name: 'Keyboard Tester',
    description: 'Interactive key matrix visualizer to check if all computer keyboard keys are functioning.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Keyboard',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'microphone-tester',
    name: 'Microphone Tester',
    description: 'Test audio input levels, frequency response, and record test clips from your mic.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Mic',
    actionType: 'generic'
  },
  {
    id: 'speaker-tester',
    name: 'Speaker & Headphone Tester',
    description: 'Test left and right stereo audio channels, bass frequencies, and speaker quality.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Volume2',
    actionType: 'generic'
  },
  {
    id: 'camera-tester',
    name: 'Webcam Tester',
    description: 'Check webcam video feed resolution, frame rate (FPS), color balance, and mirror view.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Camera',
    actionType: 'generic'
  },
  {
    id: 'screen-resolution',
    name: 'Screen Resolution Checker',
    description: 'Check display screen resolution, pixel ratio (DPR), color depth, and monitor specs.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Monitor',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'viewport-checker',
    name: 'Viewport Size Checker',
    description: 'Inspect active browser window inner dimensions, orientation, and responsive breakpoints.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Maximize2',
    actionType: 'generic'
  },
  {
    id: 'internet-speed',
    name: 'Internet Speed Test',
    description: 'Benchmark internet latency, ping times, upload and download bandwidth estimates.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Wifi',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'clipboard-viewer',
    name: 'Clipboard Content Viewer',
    description: 'Inspect text or HTML data stored in system clipboard memory safety buffer.',
    miscCategory: 'hardware-testers',
    miscCategoryLabel: 'Hardware & Screen Testers',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Clipboard',
    actionType: 'generic'
  },

  // --- CLOCKS & TIMERS (7 Tools) ---
  {
    id: 'stopwatch',
    name: 'Online Stopwatch',
    description: 'Millisecond-accurate digital stopwatch with lap split time logging.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Timer',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'countdown-timer',
    name: 'Countdown Timer',
    description: 'Set alarm countdown timers for cooking, presentations, and tasks.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Clock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'pomodoro-timer',
    name: 'Pomodoro Productivity Timer',
    description: 'Boost focus with 25-minute work intervals and short/long break audio alerts.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'calendar-tool',
    name: 'Interactive Calendar',
    description: 'Full year and month calendar view with day count and holiday planners.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Calendar',
    actionType: 'generic'
  },
  {
    id: 'timezone-clock',
    name: 'Timezone Converter Clock',
    description: 'Compare current local time across major global timezones and UTC offsets.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'digital-clock',
    name: 'Full Screen Digital Clock',
    description: 'Clean, distraction-free digital clock display with 12/24 hour toggle and seconds.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Watch',
    actionType: 'generic'
  },
  {
    id: 'world-clock',
    name: 'World Clock Grid',
    description: 'Live multi-city world clock dashboard displaying business hours worldwide.',
    miscCategory: 'clocks-timers',
    miscCategoryLabel: 'Clocks & Timers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Compass',
    actionType: 'generic'
  },

  // --- ENCODINGS, MATH & CONVERSIONS (17 Tools) ---
  {
    id: 'morse-generator',
    name: 'Morse Code Generator',
    description: 'Translate plain text to Morse code signals with audio sound playback.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Radio',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'ascii-art-generator',
    name: 'ASCII Art Generator',
    description: 'Convert text strings into stylized banner ASCII text art fonts.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'FileCode',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'ascii-table',
    name: 'ASCII Reference Table',
    description: 'Interactive character reference table showing ASCII codes, Hex, and Octal values.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Table',
    actionType: 'generic'
  },
  {
    id: 'unicode-table',
    name: 'Unicode Table & Lookup',
    description: 'Search Unicode symbols, code points, UTF-8 byte encodings, and special characters.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Search',
    actionType: 'generic'
  },
  {
    id: 'roman-numeral-converter',
    name: 'Roman Numeral Converter',
    description: 'Convert standard numbers to Roman Numerals (e.g., 2026 -> MMXXVI) and vice versa.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'BookOpen',
    actionType: 'generic'
  },
  {
    id: 'binary-converter',
    name: 'Binary Converter',
    description: 'Convert decimal numbers and text strings into 8-bit binary code representation.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Binary',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'hex-converter',
    name: 'Hexadecimal Converter',
    description: 'Convert values between Hexadecimal, Decimal, Binary, and Text formats.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'octal-converter',
    name: 'Octal Converter',
    description: 'Convert base-8 octal values to base-10 decimal, binary, and hex values.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'fraction-calculator',
    name: 'Fraction Calculator',
    description: 'Add, subtract, multiply, and divide fractions with step-by-step simplification.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Percent',
    actionType: 'generic'
  },
  {
    id: 'scientific-notation',
    name: 'Scientific Notation Converter',
    description: 'Convert standard numbers into scientific exponential notation (E-notation).',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Calculator',
    actionType: 'generic'
  },
  {
    id: 'equation-solver',
    name: 'Basic Equation Solver',
    description: 'Solve linear equations (ax + b = c) and quadratic formulas with real roots.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Sigma',
    actionType: 'generic'
  },
  {
    id: 'prime-checker',
    name: 'Prime Number Checker',
    description: 'Test whether any integer is prime and find its smallest prime factors.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'CheckCircle',
    actionType: 'generic'
  },
  {
    id: 'prime-generator',
    name: 'Prime Number Generator',
    description: 'Generate list of prime numbers up to any custom limit using Sieve algorithm.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'ListOrdered',
    actionType: 'generic'
  },
  {
    id: 'factor-calculator',
    name: 'Factor Calculator',
    description: 'Find all positive factors, factor pairs, and prime factorizations of a number.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Layers',
    actionType: 'generic'
  },
  {
    id: 'gcd-lcm-calculator',
    name: 'GCD & LCM Calculator',
    description: 'Calculate Greatest Common Divisor (GCD) and Least Common Multiple (LCM) for multiple numbers.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Divide',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'percentage-difference',
    name: 'Percentage Difference Calculator',
    description: 'Calculate percentage change, difference, and relative error between two values.',
    miscCategory: 'converters-math',
    miscCategoryLabel: 'Encodings & Math',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'TrendingUp',
    actionType: 'generic'
  }
];
